<template>
  <page-view :menu="menu">
    <template #header>
      <base-header :title="$t('message.settings')" />
    </template>

    <template #content>
      <v-row no-gutters>
        <v-col cols="12">
          <v-row align="center" no-gutters>
            <div class="icon-container mr-3">
              <v-icon color="turquisePanda" class=""
                >mdi-application-brackets</v-icon
              >
            </div>
            <span class="text-h5">Webhooks</span>
            <span class="caption ml-0 ml-sm-3 text--secondary"
              >| {{ $t('message.pages.webhooks.title') }}</span
            >
          </v-row>
        </v-col>
        <v-col class="mt-10 pa-0" cols="12">
          <v-row align="center">
            <v-col cols="2" style="min-width: 210px">
              <span class="text-body-1 font-weight-bold">{{
                $t('message.pages.webhooks.configureWebhooks')
              }}</span>
            </v-col>
            <v-col>
              <v-divider></v-divider>
            </v-col>
          </v-row>
          <v-row no-gutters class="mt-4">
            <v-col cols="12">
              <span class="body-2">
                {{ $t('message.pages.webhooks.description') }}
              </span>
            </v-col>
          </v-row>
          <v-row no-gutters class="mt-8">
            <v-card v-if="loadings.fetchData" flat width="700" height="250">
              <loading-logo :value="200" />
            </v-card>
            <v-card
              v-else-if="errors.fetchData"
              class="d-flex flex-row justify-center align-center"
              height="250"
              flat
            >
              <v-row no-gutters justify="center" align="center">
                <v-col cols="8" class="d-flex justify-center align-center mb-3">
                  <span class="text-h5">{{
                    $t('message.pages.webhooks.tryAgain')
                  }}</span>
                </v-col>
                <v-col cols="12">
                  <v-row no-gutters align="center" justify="center">
                    <v-btn
                      color="primary"
                      class="ma-2 ml-3 white--text"
                      rounded
                      @click="getAccountWebhook"
                    >
                      <span class="mx-2">{{ $t('message.tryAgain') }}</span>
                      <v-icon right dark> mdi-cached </v-icon>
                    </v-btn>
                  </v-row>
                </v-col>
              </v-row>
            </v-card>
            <v-card v-else width="100%" flat>
              <v-col cols="12" class="mb-0 pb-0 pa-0">
                <v-text-field
                  v-model="webhook.url"
                  outlined
                  persistent-hint
                  :placeholder="$t('message.pages.webhooks.addWebhook')"
                  dense
                  :loading="loadings.addWebhook || loadings.updateWebhook"
                  type="text"
                  :disabled="
                    loadings.addWebhook ||
                    loadings.deleteWebhook ||
                    !hasEditWebhookPerm
                  "
                  :readonly="inputDisabled"
                  :append-icon="inputDisabled ? '' : 'mdi-send'"
                  clear-icon="mdi-close-circle"
                  clearable
                  :error="
                    errors.onAddRefer ||
                    errors.onAddReferDuplicate ||
                    errors.onDeleteAllowedRefer ||
                    errors.onAddReferAllowedBlank ||
                    errors.invalidUrl
                  "
                  @click:append="handleSaveWebhooks"
                  @keyup.enter="handleSaveWebhooks"
                  @click:clear="handleDeleteWebhook"
                >
                  <template v-slot:message="{ message }">
                    <span
                      class="error--text caption"
                      v-if="errors.onAddRefer || errors.invalidUrl"
                    >
                      <v-icon color="error" size="18">
                        mdi-alert-circle
                      </v-icon>
                      {{ message }} {{ unknownErrorMsg }}
                    </span>
                  </template>
                </v-text-field>
              </v-col>

              <v-row>
                <v-col cols="4" v-if="logsRaw.length > 0">
                  <v-text-field
                    outlined
                    dense
                    :label="$t('message.pages.webhooks.search')"
                    v-model="searchText"
                  ></v-text-field>
                  <v-col cols="12" v-if="logs.length === 0">
                    <v-row justify="center" align="center">
                      <span class="caption">{{
                        $t('message.pages.webhooks.emptyLogResult')
                      }}</span>
                    </v-row>
                  </v-col>
                  <v-list dense>
                    <v-list-item-group v-model="selectedItem" color="primary">
                      <v-list-item
                        v-for="(item, i) in logs"
                        :key="`${item.date}-${i}`"
                      >
                        <v-list-item-icon>
                          <v-icon
                            :color="
                              parseInt(item.code / 100) === 2
                                ? 'success'
                                : 'error'
                            "
                            v-text="
                              parseInt(item.code / 100) === 2
                                ? 'mdi-check-circle'
                                : 'mdi-alert-circle'
                            "
                          ></v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>{{
                            item.request.action
                          }}</v-list-item-title>
                          <v-list-item-subtitle>{{
                            formatListItem(item)
                          }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list-item-group>
                  </v-list>
                </v-col>
                <v-divider vertical></v-divider>
                <v-col cols="8" v-if="selectedItem !== null">
                  <v-row align="center" justify="center" class="ma-0 pa-3">
                    <span style="font-size: 0.6rem">{{ activeItem.url }}</span>
                    <v-spacer></v-spacer>
                    <v-chip-legacy
                      class="ma-2"
                      :color="
                        parseInt(activeItem.code / 100) === 2
                          ? 'success'
                          : 'error'
                      "
                    >
                      {{ activeItem.code }}
                    </v-chip-legacy>
                  </v-row>
                  <v-col cols="12">
                    <span class="font-weight-bold">Request</span>
                    <json-viewer
                      show-double-quotes
                      copyable
                      theme="table"
                      :value="activeItem.request || ''"
                    ></json-viewer>
                  </v-col>
                  <v-divider></v-divider>
                  <v-col cols="12">
                    <span class="font-weight-bold">Response</span>
                    <json-viewer
                      show-double-quotes
                      copyable
                      theme="table"
                      :value="activeItem.response || ''"
                    ></json-viewer>
                  </v-col>
                </v-col>
                <v-col cols="8" v-else>
                  <v-row align="end" justify="center" class="ma-0 pa-3 pt-16">
                    <span class="text--secondary">{{
                      $t('message.pages.webhooks.emptyStateRequest')
                    }}</span>
                  </v-row>
                </v-col>
              </v-row>
            </v-card>
          </v-row>
        </v-col>
      </v-row>
    </template>
  </page-view>
</template>

<script>
// external imports
import { format } from 'date-fns'
import JsonViewer from 'vue-json-viewer'

// internal imports
import linkHelper from '@/utils/linkHelper.mixin.js'

// API
import webhooksApi from '@/services/webhooksApi'

// Components
import LoadingLogo from '@/components/LoadingLogo.vue'
import PageView from '@/layout/PageView.vue'

export default {
  name: 'Webhooks',
  mixins: [linkHelper, JsonViewer],
  components: {
    LoadingLogo,
    PageView,
    BaseHeader: () => import('@/components/BaseHeader.vue'),
  },
  data: () => ({
    loadings: {
      fetchData: false,
      saveWebhooks: false,
      deleteWebhook: false,
      addWebhook: false,
    },
    url: '',
    searchText: '',
    inputDisabled: false,
    selectedItem: null,
    errors: {
      fetchData: false,
      onAddWebhook: false,
      invalidUrl: false,
    },
    webhook: {},
    message: '',
    logsRaw: [],
  }),
  computed: {
    hasEditWebhookPerm() {
      return (
        this.$store.state.user?.userPermissions?.includes('put-user') ?? false
      )
    },
    menu() {
      return this.$store.state.user.settingsNavItems
    },
    activeItem() {
      return this.logs[this.selectedItem]
    },
    logs() {
      return this.logsRaw.filter((item) => {
        const hasItem = !!Object.keys(item).find((key) => {
          return item[key]
            .toString()
            .toLowerCase()
            .includes(this.searchText.toLowerCase())
        })
        const hasItemKeysRequest = !item.request
          ? false
          : Object.keys(item.request).find((key) => {
              return item.request[key]
                .toString()
                .toLowerCase()
                .includes(this.searchText.toLowerCase())
            })
        const hasItemKeysResponse = !item.response
          ? false
          : Object.keys(item.response).find((key) => {
              return item.response[key]
                .toString()
                .toLowerCase()
                .includes(this.searchText.toLowerCase())
            })

        return hasItem || hasItemKeysRequest || hasItemKeysResponse
      })
    },
  },
  created() {},
  async mounted() {
    await this.getAccountWebhook()
  },
  methods: {
    formatListItem(item) {
      return `${format(new Date(item.date), 'yyyy/MM/dd HH:mm')}`
    },
    async getAccountWebhook() {
      try {
        this.loadings.fetchData = true
        const ret = await webhooksApi.getWebhooks()
        if (ret.data.webhook) {
          this.webhook = ret.data.webhook
          this.inputDisabled = true

          if (this.webhook.url === '' || this.webhook.url === null) {
            delete this.webhook.url
            this.inputDisabled = false
          }
        } else {
          this.inputDisabled = false
        }
        if (ret.data.logs) {
          this.logsRaw = ret.data.logs
            .map((item) => ({
              request: 'Request',
              response: 'Response',
              ...item,
            }))
            .sort((a, b) => new Date(b.date) - new Date(a.date))
        }
        this.errors.fetchData = false
        this.errors.invalidUrl = false
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.webhook = {}
          this.errors.fetchData = false
        } else {
          this.errors.fetchData = true
        }
      }
      this.loadings.fetchData = false
    },
    async handleDeleteWebhook() {
      try {
        this.loadings.deleteWebhook = true
        if (this.webhook.id) await webhooksApi.deleteWebhook(this.webhook.id)
        this.errors.fetchData = false
        this.inputDisabled = false
      } catch (error) {
        this.errors.fetchData = true
      }
      this.loadings.deleteWebhook = false
      this.errors.invalidUrl = false
    },
    async handleSaveWebhooks() {
      try {
        const urlRegex =
          /(https?:\/\/)?([\da-z\\.-]+)\.([a-z]{2,6})([\\/\w\\.-]*)*\/?/g
        if (
          this.webhook.url &&
          this.webhook.url !== '' &&
          !urlRegex.test(this.webhook.url)
        ) {
          this.errors.invalidUrl = true
          this.message = 'Invalid URL'
          return
        }

        this.loadings.addWebhook = true
        await this.handleDeleteWebhook()
        const ret = await webhooksApi.createWebhook({ url: this.webhook.url })
        this.webhook = ret.data
        this.logsRaw = []
        this.errors.onAddWebhook = false
        this.inputDisabled = true
      } catch (error) {
        this.errors.onAddWebhook = true
      }
      this.loadings.addWebhook = false
    },
  },
}
</script>
<style lang="scss">
.table {
  white-space: nowrap;
  font-size: 14px;

  .jv-ellipsis {
    color: #999;
    display: inline-block;
    line-height: 0.9;
    font-size: 0.9em;
    padding: 0px 4px 2px 4px;
    border-radius: 3px;
    vertical-align: 2px;
    cursor: pointer;
    user-select: none;
  }
  .jv-button {
    color: #49b3ff;
  }
  .jv-key {
    color: #111111;
  }
  .jv-item {
    &.jv-array {
      color: #111111;
    }
    &.jv-boolean {
      color: #fc1e70;
    }
    &.jv-function {
      color: #067bca;
    }
    &.jv-number {
      color: #fc1e70;
    }
    &.jv-number-float {
      color: #fc1e70;
    }
    &.jv-number-integer {
      color: #fc1e70;
    }
    &.jv-object {
      color: #111111;
    }
    &.jv-undefined {
      color: #e08331;
    }
    &.jv-string {
      color: var(--v-success-base);
      word-break: break-word;
      white-space: normal;
    }
  }
  .jv-code {
    .jv-toggle {
      &:before {
        padding: 0px 2px;
        border-radius: 2px;
      }
      &:hover {
        &:before {
          background: #eee;
        }
      }
    }
  }
}

.bg-dark {
  .table {
    background-color: var(--v-neutral-darken2);
    .jv-key {
      color: var(--v-neutral-white);
    }
    .jv-item {
      &.jv-array {
        color: var(--v-neutral-white);
      }
      &.jv-object {
        color: var(--v-neutral-white);
      }
      &.jv-key {
        color: var(--v-neutral-white);
      }
    }
  }
}

.labelPercent {
  padding: 5px 8px;
  color: #fff;
}
</style>
